import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useAtom, useSetAtom } from "jotai";
import { DocumentsOperations } from "constant-data/documentsData";

import { Row } from "components/Row/Row";
import { SearchInput } from "components/SearchInput/SearchInput";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { MediumText } from "components/MediumText/MediumText";
import { Divider } from "components/Divider/Divider";
import { Paragraph } from "components/Paragraph/Paragraph";

import {
  getAndSetPrintTemplatesAtom,
  printTemplatesAtom,
  usePrintTemplatesState,
} from "../../../atoms/printTemplatesAtom";
import { PrintTemplate } from "../../../types";
import {
  setConfirmationModalAtom,
  setModalCloseAtom,
} from "../../../atoms/modalAtom/modalAtom";

import styles from "../styles.module.scss";

export const PrintTemplatesBody = (): JSX.Element => {
  const [searchText, setSearchText] = useState<string>("");
  const { printTemplatesState, setPrintTemplatesState } =
    usePrintTemplatesState();
  const [printTemplates] = useAtom(printTemplatesAtom);
  const getAndSetPrintTemplates = useSetAtom(getAndSetPrintTemplatesAtom);
  const openConfirmModal = useSetAtom(setConfirmationModalAtom);
  const closeModal = useSetAtom(setModalCloseAtom);

  const handleRowClick = (printTemplate: PrintTemplate) => {
    if (
      printTemplatesState.activePrintTemplate?.template_id ===
      printTemplate.template_id
    ) {
      openConfirmModal({
        title: "Вы хотите скачать файл этого шаблона?",
        confirmText: "Скачать",
        onConfirm: () => {
          const link = document.createElement("a");
          link.href = printTemplate.file_path;
          link.download = `florapoint_document_sheet-${moment().toISOString()}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          closeModal();
        },
      });
    } else {
      setPrintTemplatesState((prev) => ({
        ...prev,
        activePrintTemplate: printTemplate,
      }));
    }
  };

  const filteredPrintTemplates = useMemo(() => {
    return printTemplates.filter((printTemplate: PrintTemplate) =>
      printTemplate.title.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [printTemplates, searchText]);

  useEffect(() => {
    getAndSetPrintTemplates(
      printTemplatesState.activeDocumentType?.operationid || null
    ).then();
  }, [printTemplatesState.activeDocumentType?.operationid]);

  return (
    <div className={styles.container}>
      <VerticalSpace size={8} />
      <SearchInput callback={setSearchText} placeholder={"Поиск по названию"} />
      <VerticalSpace size={16} />
      <Row>
        <Paragraph width={200}>
          <MediumText>Название</MediumText>
        </Paragraph>
        <Paragraph width={200}>
          <MediumText>Дата создания</MediumText>
        </Paragraph>
        <Paragraph width={200}>
          <MediumText>Тип документа</MediumText>
        </Paragraph>
      </Row>
      <Divider />

      {filteredPrintTemplates.map((printTemplate: PrintTemplate) => (
        <Row
          key={printTemplate.template_id}
          onClick={() => handleRowClick(printTemplate)}
          isActive={
            printTemplatesState.activePrintTemplate?.template_id ===
            printTemplate.template_id
          }
        >
          <Paragraph width={200}>{printTemplate.title}</Paragraph>
          <Paragraph width={200}>
            {moment(printTemplate.createdAt).format("DD.MM.YYYY")}
          </Paragraph>
          <Paragraph width={200}>
            {DocumentsOperations.find(
              (i) => +i.id === printTemplate.doc_operation
            )?.name || printTemplate.doc_operation}
          </Paragraph>
        </Row>
      ))}
    </div>
  );
};
